import { ToTop } from '../ui/ToTop';
import { useTuck } from '../ui/useTuck';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { useRemembered } from '../lib/remember';
import { supabase } from '../lib/supabase';
import { loadShelves, type Shelves } from '../shelves/load';
import { RecentList } from '../queue/RecentList';
import { LibraryCopies } from './LibraryCopies';
import { OwnedCopies } from './OwnedCopies';
import { AddImport } from './AddImport';
import { loadAttention, type Attention } from './attention';
import { TabRow } from '../ui/TabRow';
import { todayIn } from '../book/model';
import { MY_BOOKS, MY_BOOKS_TABS, myBooksRoute, myBooksTab, startTab, type MyBooksTab } from './model';
import '../shelves/shelves.css';
import '../queue/queue.css';
import './my-books.css';

// My books (Phase 6.4 Part 1, agreed with Kristi Oct 31; filters review, Kristi Nov 2): four tabs,
// Library copies · Owned copies · Add & import · Update details. Recently added is Add & import's
// last section, and Review is Update details (No shelf yet its first list). Each tab has its own
// address, so Back from a book returns to it; the old addresses still open where they went.

type Load = { kind: 'loading' } | { kind: 'ready'; shelves: Shelves } | { kind: 'error'; message: string };

export function MyBooksPage({ wide }: { wide: boolean }) {
  // On a phone the pinned bar slides away scrolling down, back scrolling up (check-up, Kristi Nov 2).
  const tucked = useTuck(wide);
  const { path, state: routeState } = useRoute();
  // On a laptop a book opens as a side panel over this page: the address is the book's, so the
  // page keeps showing the address it was opened from.
  const here = path.startsWith(MY_BOOKS) ? path : (routeState.from ?? MY_BOOKS);
  const route = myBooksRoute(here);
  const [last, setLast] = useRemembered<MyBooksTab>('my_books:tab', 'library');
  const tab = route.tab ?? startTab(last);
  // A plain /my-books (or an address it doesn't know) becomes the tab's own address.
  // Before the page is drawn, so leaving straight away still remembers the tab.
  useLayoutEffect(() => {
    if (!path.startsWith(MY_BOOKS)) return;
    if (!route.tab) navigate(myBooksTab(tab).path, { replace: true });
    else if (route.tab !== last) setLast(route.tab);
  }, [path]);

  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [readTypes, setReadTypes] = useState<string[]>([]);
  const [toolsSlot, setToolsSlot] = useState<HTMLElement | null>(null);
  const [panelSlot, setPanelSlot] = useState<HTMLElement | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const [attention, setAttention] = useState<Attention>({});
  const load = () =>
    loadShelves()
      .then((shelves) => {
        setState({ kind: 'ready', shelves });
        void loadAttention(shelves, todayIn())
          .then(setAttention)
          .catch(() => null);
      })
      .catch((e: unknown) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }));
  useEffect(() => {
    void load();
    void supabase
      ?.from('read_types')
      .select('name, sort_order')
      .order('sort_order')
      .then(({ data }) => setReadTypes(((data as { name: string }[] | null) ?? []).map((r) => r.name)));
    const again = () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void load(), 250);
    };
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, []);

  // ↑ Top once you've scrolled more than a screen, as on the Queue (the shared piece; every tab since Nov 2).
  const topPill = <ToTop wide={wide} />;

  // The pinned bar's height, so Recently added's column headers pin just under it.
  const topEl = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = topEl.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--queue-top-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  return (
    <div class={`page queue my-books${wide ? '' : ' narrow-list'}${tucked ? ' tucked' : ''}`}>
      {wide ? <h1 class="visually-hidden">My books</h1> : null}
      <div class="shelves-top queue-top" ref={topEl}>
        {/* The one tab row (R9, Kristi Nov 2): short names on a phone (Library · Owned · Add · Recent · Review),
            the dot on the label's corner, the chosen tab always in view. A dot while something here
            needs you (Kristi, Oct 31); no counts on My books' tabs. */}
        <TabRow
          label="My books"
          value={tab}
          tabs={MY_BOOKS_TABS.map((t) => ({ key: t.key, name: t.name, short: t.short, need: attention[t.key] }))}
          onPick={(k) => {
            const t = myBooksTab(k);
            if (k !== tab || window.location.pathname !== t.path) navigate(t.path);
          }}
        />
        {/* Library copies' and Add & import's Expand all / Collapse all in the pinned bar, so it stays in view (Kristi, Oct 31). */}
        {tab === 'library' || tab === 'add' ? <span class="queue-slot mb-bar-end" ref={setToolsSlot} /> : null}
        {/* ↑ Top in the bar on the tabs without their own tools (check-up, Nov 2). */}
        {tab === 'library' || tab === 'add' || tab === 'review' ? topPill : null}
        {/* Owned copies (Part 3): its Filters, Sort and Select several here, and the Filters panel under them. */}
        {tab === 'owned' ? (
          <>
            <span class="queue-slot" ref={setToolsSlot} />
            <span class="queue-slot" ref={setPanelSlot} />
          </>
        ) : null}
      </div>

      {tab === 'library' ? (
        state.kind === 'loading' ? (
          <p class="shelves-quiet">Loading your books…</p>
        ) : state.kind === 'error' ? (
          <div class="heads-up">Couldn’t load your books: {state.message}</div>
        ) : (
          <LibraryCopies wide={wide} shelves={state.shelves} barSlot={toolsSlot} />
        )
      ) : tab === 'owned' ? (
        <OwnedCopies wide={wide} slots={{ tools: toolsSlot, panel: panelSlot }} />
      ) : tab === 'add' ? (
        state.kind === 'ready' ? (
          <AddImport wide={wide} shelves={state.shelves} readTypes={readTypes} barSlot={toolsSlot} toRecent={route.section === 'recent'} />
        ) : (
          <p class="shelves-quiet">Loading your books…</p>
        )
      ) : state.kind === 'loading' ? (
        <p class="shelves-quiet">Loading your books…</p>
      ) : state.kind === 'error' ? (
        <div class="heads-up">Couldn’t load your books: {state.message}</div>
      ) : (
        <RecentList wide={wide} shelves={state.shelves} readTypes={readTypes} view="details" />
      )}
      {/* The phone's floating ↑ on every tab (Owned copies has its own, above its Select several bar). */}
      {tab !== 'owned' ? <ToTop wide={wide} float /> : null}
    </div>
  );
}
